import React from 'react';
import { motion } from 'framer-motion';
import { AnatomyDiagram } from './graphics/Graphics';
export function Science() {
  return <section className="bg-offwhite py-12 md:py-20 px-5" data-id="element-535">
      <div className="max-w-[780px] mx-auto" data-id="element-536">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-10 text-center md:text-left" data-id="element-537">
          <div className="font-inter text-[12px] uppercase tracking-[0.1em] text-secondary mb-3" data-id="element-538">
            Understanding Why It Works
          </div>
          <h2 className="font-playfair text-[28px] md:text-[32px] text-ink font-semibold leading-tight mb-3" data-id="element-539">
            Why Your GP Hasn't Mentioned This — And Why That's Not Entirely
            Their Fault
          </h2>
          <div className="font-inter text-[13px] italic text-secondary" data-id="element-540">
            Dr. Sarah Mitchell, NHS Continence Specialist
          </div>
        </motion.div>

        <motion.figure initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mb-10" data-id="element-541">
          <img src="https://cdn.magicpatterns.com/uploads/sqtPqxhHjRzDwRhBA14bv2/imgi_3_1773956277-imgi_2_1764949784-1759803160-imgi_4_1748523250-1733206522-Screenshot_2024-12-03_141345.webp" alt="Clinician examining a bladder anatomical model" className="w-full h-auto rounded-xl shadow-subtle object-cover aspect-[16/9]" data-id="element-542" />
          <figcaption className="font-inter text-[12px] italic text-secondary mt-2 text-center" data-id="element-543">
            A specialist's view of bladder anatomy — the muscle is only half of
            the story.
          </figcaption>
        </motion.figure>

        {/* Two Column Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-544">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-cream rounded-xl p-6 shadow-subtle" data-id="element-545">
            <h3 className="font-inter font-semibold text-[16px] text-ink mb-3" data-id="element-546">
              The Muscle
            </h3>
            <p className="font-inter text-[15px] text-ink leading-relaxed" data-id="element-547">
              Your GP is right about one thing: the pelvic floor is a muscle.
              Like any muscle, it can weaken over time, especially after
              childbirth. Kegel exercises aim to strengthen this muscle. But if
              the muscle isn't receiving the instruction to contract at the
              right time, its strength is irrelevant.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          delay: 0.1
        }} className="bg-cream rounded-xl p-6 shadow-subtle" data-id="element-548">
            <h3 className="font-inter font-semibold text-[16px] text-ink mb-3" data-id="element-549">
              The Signal
            </h3>
            <p className="font-inter text-[15px] text-ink leading-relaxed" data-id="element-550">
              As oestrogen declines during menopause, the nerve pathways that
              tell your bladder to hold, and your pelvic floor to contract,
              begin to degrade. The signal becomes delayed. When you sneeze, the
              muscle is strong enough to hold, but the message arrives a
              fraction of a second too late.
            </p>
          </motion.div>
        </div>

        {/* Anatomy Diagram */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="my-10 rounded-xl overflow-hidden border border-divider bg-offwhite p-4 shadow-subtle" data-id="element-551">
          <AnatomyDiagram className="w-full h-auto" data-id="element-552" />
          <div className="text-center font-inter text-[12px] italic text-secondary mt-2" data-id="element-553">
            Fig. 1 — The signal pathway from brain to pelvic floor.
          </div>
        </motion.div>

        {/* Centered Statement */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="my-10 text-center px-4" data-id="element-554">
          <p className="font-playfair italic text-[20px] text-ink leading-relaxed" data-id="element-555">
            "A strong pelvic floor with a delayed signal produces the same
            result as a weak pelvic floor with a normal signal."
          </p>
        </motion.div>

        {/* Body Copy */}
        <div className="font-inter text-[16px] text-ink leading-[1.75] space-y-5 mb-10" data-id="element-556">
          <p data-id="element-557">
            This is why you can do Kegels until you are blue in the face and
            still leak when you laugh. You are treating the hardware, but the
            software is glitching.
          </p>
          <p data-id="element-558">
            General practitioners have ten minutes per patient. They are trained
            to offer the most basic, non-invasive advice first: pads and
            exercises. They simply do not have the time or the specialist
            training to discuss transdermal nerve pathway support.
          </p>
        </div>

        {/* Comparison Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-cream rounded-xl p-6 md:p-8 shadow-subtle" data-id="element-559">
          <h3 className="font-inter font-semibold text-[18px] text-ink mb-4" data-id="element-560">
            Why pads don't solve the problem — and may make it worse over time
          </h3>
          <div className="font-inter text-[15px] text-ink leading-relaxed space-y-4" data-id="element-561">
            <p data-id="element-562">
              Pads are a brilliant invention for managing a symptom. But they
              are a terrible long-term strategy.
            </p>
            <p data-id="element-563">
              When you rely on a pad, you subconsciously stop trying to hold.
              Your brain learns that the safety net is there, and the
              nerve-muscle connection degrades even faster through lack of use.
              It is a self-fulfilling prophecy. The more you wear them, the more
              you need them.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}